<style lang="scss">
  @use "../../../../assets/css/design-system/utils";
  @use "../../../../assets/css/design-system/colors";

  label {
    all: unset;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    .padding {
      padding-bottom: utils.in_rem(12px);
    }

    .inner {
      overflow-y: auto;
      &.restrict-height {
        max-height: utils.in_rem(200px);
      }
    }
  }

  .required {
    @include colors.color(systemError_100);
  }
</style>

<script>
  import Search from "../../../atoms/Search/Search.svelte";
  import Display from "../Display.svelte";
  import Dropdown from "../Dropdown.svelte";
  import ListGroup from "../ListGroup.svelte";
  import ListItem from "../ListItem.svelte";
  import NoItems from "../NoItems.svelte";
  import Separator from "../Separator.svelte";

  import HelperText from "../../../atoms/InputLabels/HelperText.svelte";
  import Label from "../../../atoms/InputLabels/Label.svelte";

  let cls = "";
  export { cls as class };

  // Props for the label above the dropdown
  export let label = "";
  export let labelSize = "small";
  export let tooltipMessage = "";

  // Props for the helper text below the dropdown
  export let helperText = "";
  export let state = "";

  export let leadingIcon = ""; // Icon to display on the left of the dropdown
  export let placeholder = "Select"; // Text to display when no item is selected
  export let placeholderIcon = ""; // Icon to display when no items are selected
  export let groups = []; // All options to choose from, split by group (use title and items:[ id, label, and icon ])
  export let selected; // Id of the selected option (you can bind to this)
  export let showFilter = false; // Search bar at top of dropdown
  export let showDescription = false; // Whether to show descriptions for items
  export let restrictHeight = false; // Whether to restrict height of dropdown

  // Filter items based on search query
  let searchQuery = "";

  const filterGroupsBasedOnSearch = (value) =>
    groups
      .map(({ title, items }) => ({
        title,
        items: items.filter(
          (item) =>
            item.label.toLowerCase().includes(value) ||
            item.id.toLowerCase().includes(value) ||
            title.toLowerCase().includes(value)
        ),
      }))
      .filter(({ items }) => items.length > 0);

  $: filteredGroups =
    groups && filterGroupsBasedOnSearch(searchQuery.toLowerCase());

  let focusSearch;
  const dropdownClick = () => {
    if (focusSearch) focusSearch();

    const selectedItemElement = document.querySelector(
      ".inner [aria-selected=true]"
    );
    if (selectedItemElement) {
      selectedItemElement.scrollIntoView({
        behavior: "auto",
        block: "center",
      });
    }
    searchQuery = "";
  };

  // Update Display component variables based on selected item
  let displayText = "";
  let displayIcon = "";
  let displayTextDescription = "";
  let displayTextRequired = false;
  $: {
    const selectedItem = groups
      .flatMap((group) => group.items)
      .find((item) => item.id === selected);
    displayText = selectedItem?.label || placeholder;
    displayIcon = selectedItem?.icon || placeholderIcon;
    displayTextDescription = showDescription
      ? selectedItem?.description || ""
      : "";
    displayTextRequired = selectedItem?.required || false;
  }

  let closeDropdown;

  const dropdownProps = {
    style: "width: 100%",
    leadingIcon,
    kind: "input-field",
    size: "large",
    onclick: dropdownClick,
  };
</script>

<label class={cls} for="dropdown">
  {#if label}
    <Label {label} {labelSize} {tooltipMessage} />
  {/if}

  <Dropdown {...dropdownProps} bind:close={closeDropdown} {...$$restProps}>
    <Display
      slot="button"
      {displayIcon}
      {displayText}
      {displayTextDescription}
      {displayTextRequired}
    />

    <span slot="header" class:padding={showFilter}>
      {#if showFilter}
        <Search
          bind:value={searchQuery}
          bind:focus={focusSearch}
          class="flex-0-0"
          style="z-index: 1"
        />
      {/if}
    </span>

    <div class="inner" class:restrict-height={restrictHeight}>
      {#each filteredGroups as group, index}
        <ListGroup title={group.title}>
          {#each group.items as item}
            <ListItem
              icon={item.icon}
              selected={selected === item.id}
              onclick={() => {
                selected = item.id;
                closeDropdown();
              }}
              description={showDescription && item.description
                ? item.description
                : ""}
            >
              {item.label}
              {#if item.required}
                <span class="required">*</span>
              {/if}
            </ListItem>
          {/each}
        </ListGroup>
        {#if index !== filteredGroups.length - 1}
          <Separator />
        {/if}
      {/each}
    </div>

    <!-- No items found from filter -->
    {#if filteredGroups.length === 0}
      <NoItems />
    {/if}
  </Dropdown>

  {#if helperText}
    <HelperText {helperText} {state} />
  {/if}
</label>
